/**
 * Offline WordNet thesaurus via wordpos-web dict files (served from
 * `public/wordpos/dict`, copied on postinstall). Lazy-loads POS index+data
 * modules on first look-up so the main bundle stays small.
 *
 * Related chips: same-synset lemmas, similar-to (`&`), and also-see (`^`).
 * Antonyms (`!`) stay separate so they are not mixed into synonym chips.
 */

import { isNothing, just, nothing, type Maybe } from "./maybe";

export type WordSense = {
  /** WordNet POS letter: n / v / a / s / r */
  pos: string;
  /** Short gloss (definition only, no examples). */
  def: string;
  /**
   * Same-synset + similar-to + also-see lemmas; query word filtered out;
   * underscores turned into spaces.
   */
  synonyms: string[];
  /** Antonym lemmas from `!` pointers (query filtered out). */
  antonyms: string[];
};

export type WordLookupResult = {
  query: string;
  /**
   * Base lemma found in WordNet when the query was an inflected form
   * (e.g. query "examined" → resolvedLemma "examine").
   */
  resolvedLemma?: string;
  senses: WordSense[];
};

type PosKey = "n" | "v" | "a" | "r";

const POS_NAMES: Record<PosKey, string> = {
  n: "noun",
  v: "verb",
  a: "adj",
  r: "adv",
};

const POS_KEYS = Object.keys(POS_NAMES) as PosKey[];

/** WordNet pointer symbols expanded into related chips. */
const PTR_SIMILAR_TO = "&";
const PTR_ALSO_SEE = "^";
const PTR_ANTONYM = "!";

type DictMaps = {
  index: Record<string, string>;
  data: Record<string, string>;
};

const indexCache = new Map<PosKey, Promise<Record<string, string>>>();
const dataCache = new Map<PosKey, Promise<Record<string, string>>>();

function dictBaseUrl(): string {
  const base = String(import.meta.env.BASE_URL ?? "/");
  const trimmed = base.endsWith("/") ? base : `${base}/`;
  return `${trimmed}wordpos/dict`;
}

function loadDictModule(
  cache: Map<PosKey, Promise<Record<string, string>>>,
  pos: PosKey,
  kind: "index" | "data",
): Promise<Record<string, string>> {
  const pending = cache.get(pos);
  if (typeof pending !== "undefined") return pending;

  const name = POS_NAMES[pos];
  const root = dictBaseUrl();
  const load = import(/* @vite-ignore */ `${root}/${kind}.${name}.js`)
    .then((mod) => (mod as { default: Record<string, string> }).default)
    .catch((err) => {
      cache.delete(pos);
      throw err;
    });
  cache.set(pos, load);
  return load;
}

function loadIndex(pos: PosKey): Promise<Record<string, string>> {
  return loadDictModule(indexCache, pos, "index");
}

function loadData(pos: PosKey): Promise<Record<string, string>> {
  return loadDictModule(dataCache, pos, "data");
}

async function loadPos(pos: PosKey): Promise<DictMaps> {
  const [index, data] = await Promise.all([loadIndex(pos), loadData(pos)]);
  return { index, data };
}

/** WordNet lemma form: lowercase, spaces → underscores. */
export function normalizeLemma(word: string): string {
  return word.trim().toLowerCase().replace(/\s+/g, "_");
}

function zeroPad(offset: string | number): string {
  const s = String(offset);
  return s.length >= 8 ? s : "00000000".slice(s.length) + s;
}

/** Parse a WordNet index line into synset offsets (wordpos-compatible). */
export function indexOffsets(line: string, lemma: string): string[] {
  const tokens = [lemma, ...line.split(/\s+/)];
  const ptrCount = parseInt(tokens[3] ?? "0", 10);
  const synsetCnt = parseInt(tokens[2] ?? "0", 10);
  if (Number.isNaN(ptrCount) || Number.isNaN(synsetCnt) || synsetCnt <= 0) {
    return [];
  }
  const offsets: string[] = [];
  for (let i = 0; i < synsetCnt; i++) {
    const off = tokens[ptrCount + 6 + i];
    if (typeof off === "string" && off.length > 0) offsets.push(off);
  }
  return offsets;
}

export type ParsedDataLine = {
  pos: string;
  def: string;
  synonyms: string[];
  /** Synset offsets for similar-to (`&`) and also-see (`^`). */
  relatedOffsets: string[];
  /** Synset offsets for antonym (`!`). */
  antonymOffsets: string[];
};

/** Parse a WordNet data line (offset already prepended). */
export function parseDataLine(line: string): Maybe<ParsedDataLine> {
  const parts = line.split("| ");
  if (parts.length < 2) return nothing;
  const tokens = parts[0]!.split(/\s+/);
  const gloss = parts[1] ?? "";
  const wCnt = parseInt(tokens[3] ?? "0", 16);
  if (Number.isNaN(wCnt) || wCnt <= 0) return nothing;

  const synonyms: string[] = [];
  for (let i = 0; i < wCnt; i++) {
    const lemma = tokens[4 + i * 2];
    if (typeof lemma === "string" && lemma.length > 0) {
      synonyms.push(lemma.replace(/_/g, " "));
    }
  }

  const ptrOffset = (wCnt - 1) * 2 + 6;
  const ptrCount = parseInt(tokens[ptrOffset] ?? "0", 10);
  const relatedOffsets: string[] = [];
  const antonymOffsets: string[] = [];
  if (Number.isNaN(ptrCount) === false && ptrCount > 0) {
    for (let i = 0; i < ptrCount; i++) {
      const base = ptrOffset + 1 + i * 4;
      const symbol = tokens[base];
      const offset = tokens[base + 1];
      if (typeof offset !== "string" || offset.length === 0) continue;
      if (symbol === PTR_SIMILAR_TO || symbol === PTR_ALSO_SEE) {
        relatedOffsets.push(zeroPad(offset));
      } else if (symbol === PTR_ANTONYM) {
        antonymOffsets.push(zeroPad(offset));
      }
    }
  }

  const definition = gloss.split("; ")[0]?.trim() ?? "";
  const pos = tokens[2] ?? "";
  if (pos.length === 0 || definition.length === 0) return nothing;
  return just({
    pos,
    def: definition,
    synonyms,
    relatedOffsets,
    antonymOffsets,
  });
}

function pushUniqueLemma(
  word: string,
  excludeLower: Set<string>,
  seen: Set<string>,
  out: string[],
): void {
  const key = word.toLowerCase();
  if (excludeLower.has(key) || seen.has(key)) return;
  seen.add(key);
  out.push(word);
}

function collectLemmasFromOffsets(
  maps: DictMaps,
  offsets: string[],
  excludeLower: Set<string>,
  seen: Set<string>,
): string[] {
  const out: string[] = [];
  for (const relatedOff of offsets) {
    const relatedRaw = maps.data[relatedOff];
    if (typeof relatedRaw !== "string" || relatedRaw.length === 0) continue;
    const relatedM = parseDataLine(`${relatedOff} ${relatedRaw}`);
    if (isNothing(relatedM)) continue;
    for (const syn of relatedM.value.synonyms) {
      pushUniqueLemma(syn, excludeLower, seen, out);
    }
  }
  return out;
}

function sensesForPos(
  maps: DictMaps,
  lemma: string,
  excludeLower: Set<string>,
): WordSense[] {
  const line = maps.index[lemma];
  if (typeof line !== "string" || line.length === 0) return [];
  const out: WordSense[] = [];
  for (const offset of indexOffsets(line, lemma)) {
    const padded = zeroPad(offset);
    const raw = maps.data[padded];
    if (typeof raw !== "string" || raw.length === 0) continue;
    const parsedM = parseDataLine(`${padded} ${raw}`);
    if (isNothing(parsedM)) continue;
    const parsed = parsedM.value;

    const synonymSeen = new Set<string>();
    const synonyms: string[] = [];
    for (const syn of parsed.synonyms) {
      pushUniqueLemma(syn, excludeLower, synonymSeen, synonyms);
    }
    synonyms.push(
      ...collectLemmasFromOffsets(
        maps,
        parsed.relatedOffsets,
        excludeLower,
        synonymSeen,
      ),
    );

    out.push({
      pos: parsed.pos,
      def: parsed.def,
      synonyms,
      antonyms: collectLemmasFromOffsets(
        maps,
        parsed.antonymOffsets,
        excludeLower,
        new Set(),
      ),
    });
  }
  return out;
}

/**
 * WordNet Morphy-style detach rules (suffix → replacement). Applied when the
 * exact query is missing from the index so "examined" can resolve to "examine".
 * @see https://wordnet.princeton.edu/documentation/morphy7wn
 */
const NOUN_DETACH: [string, string][] = [
  ["s", ""],
  ["ses", "s"],
  ["xes", "x"],
  ["zes", "z"],
  ["ches", "ch"],
  ["shes", "sh"],
  ["men", "man"],
  ["ies", "y"],
];

const VERB_DETACH: [string, string][] = [
  ["s", ""],
  ["ies", "y"],
  ["es", "e"],
  ["es", ""],
  ["ed", "e"],
  ["ed", ""],
  ["ing", "e"],
  ["ing", ""],
];

const ADJ_DETACH: [string, string][] = [
  ["ier", "y"],
  ["iest", "y"],
  ["er", ""],
  ["est", ""],
  ["er", "e"],
  ["est", "e"],
];

const DETACH_BY_POS: Record<PosKey, [string, string][]> = {
  n: NOUN_DETACH,
  v: VERB_DETACH,
  a: ADJ_DETACH,
  r: [],
};

/** Generate base-form candidates for `lemma` (exact form first). */
export function lemmaCandidates(lemma: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (form: string): void => {
    if (form === null || form.length === 0 || form.length < 2 || seen.has(form))
      return;
    seen.add(form);
    out.push(form);
  };
  push(lemma);
  for (const rules of Object.values(DETACH_BY_POS)) {
    for (const [suffix, repl] of rules) {
      if (lemma.length > suffix.length && lemma.endsWith(suffix)) {
        push(lemma.slice(0, -suffix.length) + repl);
      }
    }
  }
  return out;
}

export function posLabel(pos: string): string {
  switch (pos) {
    case "n":
      return "Noun";
    case "v":
      return "Verb";
    case "a":
    case "s":
      return "Adjective";
    case "r":
      return "Adverb";
    default:
      return pos.toUpperCase();
  }
}

/**
 * Look up definitions, related words, and antonyms for `word` across all POS.
 * Loads index modules first, then data only for POS that contain a candidate.
 * Inflected forms fall back to WordNet Morphy-style base lemmas.
 */
export async function lookupWord(word: string): Promise<WordLookupResult> {
  const trimmed = word.trim();
  if (trimmed === null || trimmed.length === 0)
    return { query: "", senses: [] };
  const lemma = normalizeLemma(trimmed);

  const indexes = await Promise.all(POS_KEYS.map((k) => loadIndex(k)));

  const excludeLower = new Set<string>([
    trimmed.toLowerCase(),
    lemma.replace(/_/g, " "),
  ]);

  for (const candidate of lemmaCandidates(lemma)) {
    const hitKeys = POS_KEYS.filter((_, i) => candidate in indexes[i]!);
    if (hitKeys.length === 0) continue;

    excludeLower.add(candidate.replace(/_/g, " "));
    const mapsByPos = await Promise.all(hitKeys.map((k) => loadPos(k)));
    const senses: WordSense[] = [];
    for (const maps of mapsByPos) {
      senses.push(...sensesForPos(maps, candidate, excludeLower));
    }
    if (senses.length === 0) continue;
    return {
      query: trimmed,
      senses,
      ...(candidate !== lemma
        ? { resolvedLemma: candidate.replace(/_/g, " ") }
        : {}),
    };
  }

  return { query: trimmed, senses: [] };
}

/** Test helper: drop cached dict promises. */
export function resetWordLookupCacheForTests(): void {
  indexCache.clear();
  dataCache.clear();
}
